import type { Editor } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

/**
 * The table containing the current selection, or null when the selection is outside a table.
 */
function findSelectedTable(editor: Editor): ProseMirrorNode | null {
	const { $from } = editor.state.selection;
	for (let depth = $from.depth; depth > 0; depth -= 1) {
		const node = $from.node(depth);
		if (node.type.name === "table") {
			return node;
		}
	}
	return null;
}

function countRows(table: ProseMirrorNode): number {
	let rows = 0;
	table.forEach((child) => {
		if (child.type.name === "tableRow") rows += 1;
	});
	return rows;
}

/**
 * Widest row in the table, counting a merged cell once per column it spans.
 */
function countColumns(table: ProseMirrorNode): number {
	let widest = 0;
	table.forEach((row) => {
		if (row.type.name !== "tableRow") return;
		let columns = 0;
		row.forEach((cell) => {
			columns += Number(cell.attrs.colspan) || 1;
		});
		widest = Math.max(widest, columns);
	});
	return widest;
}

/**
 * Deletes the table row containing the current selection.
 *
 * LSP-17534: "Delete Row" cleared a row's contents without removing the row. Two distinct
 * causes, both handled here:
 *
 *  1. Deleting the header row — a GFM table must serialize a header, so the markdown serializer
 *     synthesized a blank one and the row looked emptied rather than removed. We promote the
 *     next row into the header so the table actually shrinks by one.
 *  2. Deleting the only remaining row — ProseMirror refuses to delete it, because a table with
 *     zero rows is not a valid document, so the button silently did nothing. A table with no
 *     rows has nothing left to show, so we remove the table itself.
 */
export function deleteTableRow(editor: Editor | null | undefined): void {
	if (!editor) return;

	const table = findSelectedTable(editor);
	if (!table) return;

	if (countRows(table) <= 1) {
		editor.chain().focus().deleteTable().run();
		return;
	}

	const deletingHeaderRow = editor.isActive("tableHeader");
	const chain = editor.chain().focus().deleteRow();

	if (deletingHeaderRow) {
		chain.toggleHeaderRow();
	}

	chain.run();
}

/**
 * Deletes the table column containing the current selection.
 *
 * LSP-17534 (sibling path): ProseMirror refuses to delete a table's last remaining column for
 * the same reason it refuses the last row, so "Delete Column" silently did nothing on a
 * single-column table. Removing the last column leaves nothing to display, so the table goes
 * with it. Columns need no header handling — every row loses a cell and the header row stays a
 * header, so the serializer always has a header to emit.
 */
export function deleteTableColumn(editor: Editor | null | undefined): void {
	if (!editor) return;

	const table = findSelectedTable(editor);
	if (!table) return;

	if (countColumns(table) <= 1) {
		editor.chain().focus().deleteTable().run();
		return;
	}

	editor.chain().focus().deleteColumn().run();
}
